import { SettingsIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import UpgradeBadge from '../../../components/UpgradeBadge'
import { useHasEntitlement } from '../../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../../types/organization-entitlements'
import { usePaywall } from '../../../hooks/usePaywall'
import { Prescription } from '../../../types'
import { printPaperPrescription } from '../../NewScriptPage/SendScriptPanel/PaperPrescription'
import { isPrescriptionRenewableByAge, transformPrescriptions } from '../utilities'
import { printEvidenceOfPrescription } from './EvidenceOfPrescription'
import PrescribedItemStateTag from './PrescribedItemStateTag'
import { PrescriptionTypeTag } from './PrescriptionTypeTag'
import { TruncatedText } from '../../../components/shared-components/TruncatedText'
import { FiFileText } from 'react-icons/fi'
import { MdLocationOn } from 'react-icons/md'
import { analyticsAction } from '../../../common/utils/analytics'

const StyledWrapper = styled(Box)`
  border: 1px solid #555555;
  border-radius: 16px;
  max-height: 100%;
  background-color: white;
  overflow: hidden;
`

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px !important;
  }
`

interface PrescriptionTableProps {
  prescriptions: Prescription[]
  isReadonlyPermission?: boolean
  isPrescriptionAdmin: boolean
  viewerActive: boolean
  prescriberActiveById: Map<string, boolean>
  openCeaseModal: (index: number) => void
  openDispenseStatusModal: (index: number, canCease: boolean) => void
  openReIssueModal: (index: number) => void
  openCancelModal: (index: number) => void
  onRenewScript: (prescription: Prescription) => void
  renewingScid?: string | null
  shareRxAcrossOrg: boolean
  isMultipleLocationsFeatureOn: boolean
  currentUserPrescriberId?: string | null
  canRenewScript: boolean
}

const PrescriptionTable: React.FC<PrescriptionTableProps> = ({
  prescriptions,
  openCeaseModal,
  openDispenseStatusModal,
  openReIssueModal,
  openCancelModal,
  onRenewScript,
  renewingScid,
  isReadonlyPermission,
  isPrescriptionAdmin,
  viewerActive,
  prescriberActiveById,
  shareRxAcrossOrg,
  isMultipleLocationsFeatureOn,
  currentUserPrescriberId,
  canRenewScript,
}) => {
  const { hasSubscriptionError, open: openPaywall, paywallModal } = usePaywall()
  const isRenewScriptsEnabled = useHasEntitlement(FeatureKey.RENEW_SCRIPTS)
  const shouldShowPaywall = hasSubscriptionError || !isRenewScriptsEnabled

  const handleRenewClick = (prescription: Prescription) => {
    if (renewingScid) {
      return
    }
    if (shouldShowPaywall) {
      openPaywall()
      return
    }
    onRenewScript(prescription)
  }

  const viewPaperPrescription = (prescription: Prescription) => {
    printPaperPrescription(prescription)
  }

  const viewElectronicPrescription = (scid: string) => {
    window.open(`${process.env.REACT_APP_ELECTRONIC_PRESCRIPTION_BASE_URL}/${scid}`, '_blank')
  }

  return (
    <StyledWrapper>
      {paywallModal}
      <Box overflowX="auto" overflowY="auto" height="100%" width="100%">
        {prescriptions.length > 0 ? (
          <StyledTable variant="striped" size={['md', 'lg']} sx={{ tableLayout: 'fixed' }}>
            <TableCaption />
            <Thead position="sticky" top={0} backgroundColor="white" zIndex={1}>
              <Tr>
                <Th width="120px">Actions</Th>
                <Th width="150px">Patient Name</Th>
                {shareRxAcrossOrg && <Th width="150px">Prescriber Name</Th>}
                {isMultipleLocationsFeatureOn && (
                  <Th width="140px" color="gray.600" fontWeight="semibold">
                    LOCATION
                  </Th>
                )}
                <Th width="120px">SCID</Th>
                <Th width="100px">Type</Th>
                <Th width="130px">Created Date</Th>
                <Th width="220px">Prescribed Item Name | Quantity</Th>
                <Th width="120px">Original Repeats</Th>
                <Th width="200px">Patient Instructions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {prescriptions.map((prescription, index) => {
                const transformed = transformPrescriptions(prescription)
                const {
                  patientName,
                  prescriberName,
                  createdDate,
                  prescribedItems,
                  isPaperPrescription,
                  scid,
                  isEmergencySupply,
                  isSendToPharmacy,
                } = transformed
                const isItemInactive =
                  prescribedItems[0].state === 'Ceased' || prescribedItems[0].state === 'Cancelled'
                // eRx only cancels Active items and Cancel removes every item, so any item a
                // pharmacy has acted on blocks it.
                const isDispensed = prescribedItems.some((item) => item.state === 'Dispensed')
                const isHeldByPharmacy = prescribedItems.some(
                  (item) => item.state === 'Pending' || item.state === 'Deferred'
                )
                const cancelBlockedReason = isDispensed
                  ? "This script can't be cancelled because the original supply has been dispensed. To stop the remaining repeats, use Cease."
                  : isHeldByPharmacy
                  ? "This script can't be cancelled because a pharmacy has already started processing it. To stop it, use Cease."
                  : null

                const issuingPrescriberId = prescription.issueSnapshot.prescriber.id
                const issuingActive = prescriberActiveById.get(issuingPrescriberId) === true
                const isSelfPrescribed = issuingPrescriberId === currentUserPrescriberId

                const resendBlockedByPrescribers = isPrescriptionAdmin
                  ? !issuingActive
                  : !issuingActive || !viewerActive

                const ceaseCancelBlockedByPrescribers =
                  !issuingActive || !viewerActive || !isSelfPrescribed

                const canCease =
                  !isReadonlyPermission &&
                  !isPaperPrescription &&
                  !isItemInactive &&
                  !ceaseCancelBlockedByPrescribers

                const showRenewScript =
                  canRenewScript &&
                  viewerActive &&
                  isPrescriptionRenewableByAge(prescription.createdDate)

                return (
                  <Tr key={`${prescription.id}`}>
                    <Td textAlign={'center'}>
                      <Menu>
                        <MenuButton
                          aria-label={`Actions for prescription ${scid}`}
                          as={Button}
                          colorScheme="blue"
                          variant="link"
                          width={'100%'}
                          {...analyticsAction('prescription_actions_menu')}
                        >
                          <SettingsIcon />
                        </MenuButton>
                        <MenuList>
                          <Tooltip
                            label="View the electronic prescription for your patient."
                            placement="right"
                          >
                            <MenuItem
                              isDisabled={isItemInactive}
                              {...analyticsAction('prescription_view')}
                              onClick={() => {
                                isPaperPrescription
                                  ? viewPaperPrescription(prescription)
                                  : viewElectronicPrescription(scid)
                              }}
                            >
                              View
                            </MenuItem>
                          </Tooltip>
                          <Tooltip
                            label={'Print evidence of electronic prescription.'}
                            placement={'right'}
                          >
                            <MenuItem
                              isDisabled={isPaperPrescription || isItemInactive}
                              {...analyticsAction('prescription_print_eop')}
                              onClick={() => {
                                printEvidenceOfPrescription(prescription)
                              }}
                            >
                              Print EoP
                            </MenuItem>
                          </Tooltip>
                          {/* Checking is read-only, so prescription admins without a prescriber
                              profile can check too; ceasing from the result still follows canCease. */}
                          {(!isReadonlyPermission || isPrescriptionAdmin) && (
                            <Tooltip
                              label={
                                isPaperPrescription
                                  ? 'Available for electronic prescriptions only.'
                                  : 'See whether a pharmacy has dispensed this script.'
                              }
                              placement={'right'}
                            >
                              {/* isFocusable keeps a disabled item hoverable and in the menu's
                                  arrow-key navigation, so its reason stays reachable. */}
                              <MenuItem
                                isDisabled={isPaperPrescription}
                                isFocusable
                                {...analyticsAction('prescription_check_dispense_status')}
                                onClick={() => openDispenseStatusModal(index, canCease)}
                              >
                                Check dispense status
                              </MenuItem>
                            </Tooltip>
                          )}
                          {!isReadonlyPermission && (
                            <Tooltip
                              label={
                                'Clicking this button disables all remaining repeats. This button is applicable to electronic prescriptions only. The script can be ceased even after the original script has been dispensed.'
                              }
                              placement={'right'}
                            >
                              <MenuItem
                                isDisabled={
                                  isPaperPrescription ||
                                  isItemInactive ||
                                  ceaseCancelBlockedByPrescribers
                                }
                                {...analyticsAction('prescription_cease')}
                                onClick={() => openCeaseModal(index)}
                              >
                                Cease
                              </MenuItem>
                            </Tooltip>
                          )}
                          {!isReadonlyPermission && (
                            <Tooltip
                              label={
                                cancelBlockedReason ??
                                'Clicking this button cancels the entire prescription. The script can only be cancelled provided the original script has never been dispensed.'
                              }
                              placement={'right'}
                            >
                              <MenuItem
                                isDisabled={
                                  isItemInactive ||
                                  !!cancelBlockedReason ||
                                  ceaseCancelBlockedByPrescribers
                                }
                                // Focusable while disabled only when the tooltip explains why.
                                isFocusable={!!cancelBlockedReason}
                                {...analyticsAction('prescription_cancel')}
                                onClick={() => openCancelModal(index)}
                              >
                                Cancel
                              </MenuItem>
                            </Tooltip>
                          )}
                          <Tooltip
                            label={
                              'Enable the same electronic prescription to be sent to the nominated mobile number and/or email address of the patient. SCID of a re-sent prescription will be the same as the original prescription.'
                            }
                            placement={'right'}
                          >
                            <MenuItem
                              isDisabled={
                                isPaperPrescription ||
                                isItemInactive ||
                                isEmergencySupply ||
                                isSendToPharmacy ||
                                resendBlockedByPrescribers
                              }
                              {...analyticsAction('prescription_resend')}
                              onClick={() => openReIssueModal(index)}
                            >
                              Re-send
                            </MenuItem>
                          </Tooltip>
                          {showRenewScript && (
                            <Tooltip
                              label="Create a new prescription with the same details as this script."
                              placement="right"
                            >
                              <MenuItem
                                position="relative"
                                isDisabled={!!renewingScid}
                                {...analyticsAction('prescription_renew')}
                                onClick={() => handleRenewClick(prescription)}
                              >
                                {shouldShowPaywall && <UpgradeBadge />}
                                Renew script
                              </MenuItem>
                            </Tooltip>
                          )}
                        </MenuList>
                      </Menu>
                    </Td>
                    <Td>{patientName}</Td>
                    {shareRxAcrossOrg && <Td>{prescriberName}</Td>}
                    {isMultipleLocationsFeatureOn && (
                      <Td>
                        <Flex align="center" gap={2} minW={0}>
                          <Icon
                            as={MdLocationOn}
                            color="red.400"
                            boxSize={4}
                            flexShrink={0}
                            aria-hidden
                          />
                          <Box flex="1" minW={0} color="gray.800">
                            <TruncatedText text={transformed.locationName || '—'} />
                          </Box>
                        </Flex>
                      </Td>
                    )}
                    <Td>{prescription.scid}</Td>
                    <Td>
                      <PrescriptionTypeTag isPaperPrescription={isPaperPrescription} />
                    </Td>
                    <Td>{createdDate}</Td>
                    <Td whiteSpace={'normal'}>
                      {prescribedItems.map((item, idx) => (
                        <Text key={`displayName_${idx}`}>
                          {item.displayName} <PrescribedItemStateTag state={item.state} />
                        </Text>
                      ))}
                    </Td>
                    <Td>
                      {prescribedItems.map((item, idx) => (
                        <Text key={`numberOfRepeatsAuthorised_${idx}`}>
                          {item.numberOfRepeatsAuthorised}
                        </Text>
                      ))}
                    </Td>
                    <Td whiteSpace={'normal'}>
                      {prescribedItems.map((item, idx) => (
                        <TruncatedText
                          key={`patientInstructions_${idx}`}
                          text={item.patientInstructions}
                        />
                      ))}
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </StyledTable>
        ) : (
          <Center gap={2}>
            <VStack justifyContent="center" alignItems="center" spacing={4} p={8}>
              <Box w={24} color="blue.500" p={4} borderRadius="2xl" backgroundColor="blue.50">
                <FiFileText />
              </Box>
              <Text color="dark.500" fontSize="lg" fontWeight="bold">
                No prescriptions found
              </Text>
            </VStack>
          </Center>
        )}
      </Box>
    </StyledWrapper>
  )
}

export default PrescriptionTable
